import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedIncidents = ({ relatedIncidents, onLinkIncident, canEdit }) => {
  const navigate = useNavigate();

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-error text-error-foreground';
      case 'high': return 'bg-warning text-warning-foreground';
      case 'medium': return 'bg-accent text-accent-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-error text-error-foreground';
      case 'in progress': return 'bg-warning text-warning-foreground';
      case 'resolved': return 'bg-success text-success-foreground';
      case 'closed': return 'bg-muted text-muted-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getRelationshipIcon = (relationship) => {
    switch (relationship) {
      case 'duplicate': return 'Copy';
      case 'related': return 'Link';
      case 'parent': return 'ArrowUp';
      case 'child': return 'ArrowDown';
      case 'blocks': return 'Ban';
      case 'blocked_by': return 'Shield';
      default: return 'Link';
    }
  };

  const getRelationshipColor = (relationship) => {
    switch (relationship) {
      case 'duplicate': return 'text-warning';
      case 'related': return 'text-primary';
      case 'parent': return 'text-success';
      case 'child': return 'text-accent';
      case 'blocks': return 'text-error';
      case 'blocked_by': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const handleViewIncident = (incidentId) => {
    navigate(`/incident-details?id=${incidentId}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="GitBranch" size={20} />
          Related Incidents ({relatedIncidents?.length})
        </h2>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            iconName="Link"
            onClick={() => onLinkIncident()}
          >
            Link Incident
          </Button>
        )}
      </div>
      <div className="space-y-4">
        {relatedIncidents?.map((incident) => (
          <div key={incident?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex items-center gap-1 ${getRelationshipColor(incident?.relationship)}`}>
                    <Icon name={getRelationshipIcon(incident?.relationship)} size={16} />
                    <span className="text-sm font-medium capitalize">
                      {incident?.relationship?.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">#{incident?.id}</span>
                </div>
                
                <h3 className="font-medium text-foreground mb-2 line-clamp-2">
                  {incident?.title}
                </h3>
                
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getSeverityColor(incident?.severity)}`}>
                    {incident?.severity}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(incident?.status)}`}>
                    {incident?.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    <span>Created: {new Date(incident.createdAt)?.toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="User" size={12} />
                    <span>Assigned: {incident?.assignedTo || 'Unassigned'}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  onClick={() => handleViewIncident(incident?.id)}
                >
                  View
                </Button>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Unlink"
                    onClick={() => {/* Handle unlink */}}
                    className="text-error hover:text-error"
                  />
                )}
              </div>
            </div>

            {/* Relationship Details */}
            {incident?.relationshipDetails && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {incident?.relationshipDetails}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      {relatedIncidents?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="GitBranch" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No related incidents</p>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              iconName="Link"
              onClick={() => onLinkIncident()}
              className="mt-3"
            >
              Link First Incident
            </Button>
          )}
        </div>
      )}
      {/* Relationship Legend */}
      {relatedIncidents?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-3">Relationship Types</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {[
              { type: 'duplicate', desc: 'Same issue reported multiple times' },
              { type: 'related', desc: 'Connected or similar incidents' },
              { type: 'parent', desc: 'This incident caused others' },
              { type: 'child', desc: 'Caused by another incident' },
              { type: 'blocks', desc: 'Prevents resolution of others' },
              { type: 'blocked_by', desc: 'Cannot be resolved until others are fixed' }
            ]?.map(({ type, desc }) => (
              <div key={type} className="flex items-start gap-2">
                <Icon 
                  name={getRelationshipIcon(type)} 
                  size={14} 
                  className={getRelationshipColor(type)} 
                />
                <div>
                  <div className="font-medium text-foreground capitalize">
                    {type?.replace('_', ' ')}
                  </div>
                  <div className="text-muted-foreground">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedIncidents;